<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useToast } from 'primevue/usetoast'
import { Form } from '@primevue/forms';
import { FormField } from '@primevue/forms';
import Password from 'primevue/password'
import Message from 'primevue/message'
import Button from 'primevue/button'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const toast = useToast()

const uid = computed(() => String(route.query.uid || ''))
const token = computed(() => String(route.query.token || ''))

const newPwd = ref('')
const newPwd2 = ref('')
const loading = ref(false)

function required(v) { return (!v || String(v).trim() === '') ? 'Обязательное поле' : undefined }
function same(v) { return v !== newPwd.value ? 'Пароли не совпадают' : undefined }

const linkInvalid = computed(() => !uid.value || !token.value)

async function onSubmit() {
  if (linkInvalid.value) {
    toast.add({ severity: 'error', summary: 'Некорректная ссылка', life: 3000 })
    return
  }
  const p1err = required(newPwd.value)
  const p2err = required(newPwd2.value) || same(newPwd2.value)
  if (p1err || p2err) {
    toast.add({ severity: 'warn', summary: 'Исправьте ошибки формы', life: 2500 })
    return
  }
  loading.value = true
  try {
    await auth.resetPasswordConfirm({
      uid: uid.value,
      token: token.value,
      new_password: newPwd.value,
      re_new_password: newPwd2.value,
    })
    toast.add({ severity: 'success', summary: 'Пароль обновлён', detail: 'Теперь войдите с новым паролем', life: 3000 })
    await router.replace({ path: '/login' })
  } catch (e) {
    const msg = e?.response?.data?.detail
        || e?.response?.data?.non_field_errors?.[0]
        || 'Не удалось сменить пароль'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: msg, life: 4000 })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-white flex items-center justify-center p-4">
    <div class="w-full max-w-sm">
      <h1 class="text-center text-xl font-semibold mb-6">Сброс пароля</h1>

      <div v-if="linkInvalid" class="flex flex-col gap-3">
        <Message severity="error" size="small" variant="simple">
          Ссылка недействительна. Проверьте письмо или запросите новую.
        </Message>
        <div class="flex justify-center">
          <Button label="На страницу входа" @click="$router.push('/login')" />
        </div>
      </div>

      <div v-else>
        <Form class="flex flex-col gap-4 w-full">
          <FormField v-slot="$field" asChild name="new_password" :validate="() => required(newPwd)">
            <section class="flex flex-col gap-2">
              <Password v-model="newPwd" type="password" placeholder="Новый пароль" :feedback="false" toggleMask fluid />
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
                {{ $field.error?.message }}
              </Message>
            </section>
          </FormField>

          <FormField v-slot="$field" asChild name="re_new_password" :validate="() => required(newPwd2) || same(newPwd2)">
            <section class="flex flex-col gap-2">
              <Password v-model="newPwd2" type="password" placeholder="Повторите пароль" :feedback="false" toggleMask fluid />
              <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
                {{ $field.error?.message }}
              </Message>
            </section>
          </FormField>

          <div class="flex items-center justify-between">
            <Button type="button" link label="Войти" @click="$router.push('/login')" />
            <Button type="button" :loading="loading" severity="secondary" label="Обновить пароль" @click="onSubmit" />
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>